// Lire un montant tapé par un humain, en français.
//
// CE QUE CE MODULE RÉPARE. Le CRM lisait les montants ainsi :
//
//     Number(saisie.replace(',', '.')) || 0
//
// Deux défauts dans une ligne. `replace(',', '.')` ne remplace QUE la première
// virgule. Et `|| 0` transforme tout ce qui ne se lit pas — NaN compris — en
// zéro, sans un mot. Un règlement de 2 360,16 € tapé « 2 360,16 € », comme le
// CRM l'affiche lui-même, devenait un règlement de 0,00 € enregistré sans
// broncher. Trois fois de suite, sur une vraie facture.
//
// LA RÈGLE ICI : on lit largement, et on REFUSE ce qu'on n'a pas compris.
// `null` veut dire « je n'ai pas su lire », jamais « zéro ». C'est à
// l'appelant de le dire à l'utilisateur plutôt que d'enregistrer un chiffre
// qu'il n'a pas voulu.
//
// Fonctions PURES : pas d'accès réseau, pas d'horloge, tout est paramètre.

/**
 * Les espaces qui séparent les milliers. Le CRM affiche « 2 360,16 € » avec
 * une espace insécable étroite (U+202F) ; copiée-collée, elle revient telle
 * quelle. L'espace insécable ordinaire (U+00A0) vient des tableurs.
 */
const ESPACES = /[\s   ]/g;

/** Ce qui décore un montant sans le chiffrer. */
const DECORATIONS = /[€$]|EUR/gi;

/**
 * Le nombre derrière une saisie, ou `null` si elle ne se lit pas.
 *
 * Accepte ce qu'un humain écrit vraiment :
 *
 *   2360,16      2360.16      2 360,16      2 360,16 €     2.360,16
 *   +2360,16     -690,00      ,5           2 360
 *
 * Refuse le reste — y compris la chaîne vide, « abc », « 1,2,3 » — plutôt que
 * de rendre zéro.
 *
 * LES DEUX SÉPARATEURS. Quand la saisie porte un point ET une virgule, le
 * dernier des deux est le séparateur décimal : « 2.360,16 » vaut 2360,16 et
 * « 2,360.16 » vaut aussi 2360,16. Quand elle n'en porte qu'un, il est
 * décimal — « 2.360 » vaut donc 2,36 mille fois moins qu'on pourrait croire,
 * mais c'est le choix le moins surprenant : personne ne tape un séparateur de
 * milliers sans séparateur décimal derrière.
 */
export function montantSaisi(texte: string): number | null {
  if (typeof texte !== 'string') return null;
  const nu = texte.replace(ESPACES, '').replace(DECORATIONS, '');
  if (nu === '') return null;

  const dernierPoint = nu.lastIndexOf('.');
  const derniereVirgule = nu.lastIndexOf(',');
  let normalise: string;
  if (dernierPoint >= 0 && derniereVirgule >= 0) {
    // Le dernier des deux tranche : l'autre séparait les milliers.
    const [decimal, milliers] =
      derniereVirgule > dernierPoint ? [derniereVirgule, '.'] : [dernierPoint, ','];
    normalise = nu.slice(0, decimal).split(milliers).join('') + '.' + nu.slice(decimal + 1);
  } else {
    normalise = nu.replace(/,/g, '.');
  }

  // Une seule forme acceptée après normalisation : un nombre décimal simple.
  // Sans ce garde, « 1.2.3 » ou « 12e5 » passeraient par Number().
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(normalise)) return null;
  const n = Number(normalise);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/**
 * Un montant de RÈGLEMENT valide : lisible, et strictement positif.
 *
 * Un règlement de zéro n'existe pas — c'est le symptôme d'une saisie perdue,
 * pas une intention. On le refuse au lieu de l'enregistrer.
 */
export function reglementSaisi(texte: string): number | null {
  const n = montantSaisi(texte);
  return n === null || n <= 0 ? null : n;
}

/**
 * Un REMBOURSEMENT sur avoir : lisible et non nul, quel que soit son signe.
 *
 * Un avoir porte un TTC négatif ; l'argent qui en sort se saisit donc avec le
 * signe qui lui convient. Exiger un montant positif ici casserait le
 * remboursement, alors on vérifie seulement qu'il n'est ni illisible ni nul.
 */
export function remboursementSaisi(texte: string): number | null {
  const n = montantSaisi(texte);
  return n === null || n === 0 ? null : n;
}

/**
 * Ce qu'on dit à l'utilisateur quand sa saisie ne passe pas. `null` quand tout
 * va bien : il n'y a alors rien à afficher.
 *
 * `negatifAutorise` vise les avoirs : leur remboursement peut être négatif, et
 * un message qui réclamerait du positif serait un contresens.
 */
export function messageMontantInvalide(
  texte: string,
  options: { negatifAutorise?: boolean } = {},
): string | null {
  if (texte.trim() === '') return null; // pas encore saisi : on ne gronde pas
  const n = montantSaisi(texte);
  if (n === null) return 'Montant illisible. Écrivez par exemple 2360,16 ou 2 360,16 €.';
  if (n === 0) return 'Un règlement de 0 € ne veut rien dire.';
  if (n < 0 && options.negatifAutorise !== true) return 'Le montant doit être positif.';
  return null;
}
